import { BreakpointObserver } from '@angular/cdk/layout';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ConnectDeclineReason } from '@epicuro-next/data/connect';
import {
  selectConnectParams,
  selectDeclineReason,
  selectUserCurrentContactState,
  selectVideoLoadingState,
  setDeclineReason,
  setVideoLoading,
} from '@epicuro-next/state/connect-state';
import { selectUser } from '@epicuro-next/state/user-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, of, Subject, timer } from 'rxjs';
import {
  filter,
  map,
  switchMap,
  switchMapTo,
  take,
  takeUntil,
} from 'rxjs/operators';

import {
  AnimationParams,
  hostVideoAnimation,
} from '../../animation/video-container';
import { ParticipantComponent } from '../../components/participant/participant.component';
import {
  ConnectVideoStore,
  TwilioDeviceKind,
} from '../../services/connect-video.store';
import { transformToElapsedTime } from '../../shared/elapsed-time/elapsed-time';

const BREAKPOINT = '(max-width: 576px)';

// Does not work in safari
// But we want to support it on chromium browsers
// https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/setSinkId
// Bug related webkit https://bugs.webkit.org/show_bug.cgi?id=179415
declare interface CustomHTMLAudioElement extends HTMLAudioElement {
  setSinkId: (deviceId: string) => Promise<void>;
}

@Component({
  selector: 'epc-connect-video',
  templateUrl: './connect-video.component.html',
  styleUrls: ['./connect-video.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [hostVideoAnimation],
  providers: [ConnectVideoStore],
  standalone: false,
})
export class ConnectVideoComponent implements OnInit, OnDestroy {
  public loading$ = this.store.pipe(select(selectVideoLoadingState));
  public contact$ = this.store.pipe(select(selectUserCurrentContactState));
  public mediaAccessDenied$ = this.connectVideoStore.selectMediaAccessDenied;
  public localParticipant$ = this.connectVideoStore.selectLocalParticipant;
  public localParticipantMediaStatus$ =
    this.connectVideoStore.selectLocalParticipantMediaStatus;
  public remoteParticipant$ = this.connectVideoStore.selectRemoteParticipant;
  public isScreenSharing$ = this.connectVideoStore.selectIsScreenSharing;
  public isPictureInPicture$ = this.connectVideoStore.selectIsPictureInPicture;
  public isConnectionDone$ = this.connectVideoStore.selectIsConnectionDone;
  public remoteParticipantMediaStatus$ =
    this.connectVideoStore.selectRemoteParticipantMediaStatus;
  public currentUser$ = this.store.pipe(select(selectUser));
  public currentContact$ = this.store.pipe(
    select(selectUserCurrentContactState),
  );

  public lightModeActive$ = combineLatest([
    this.isScreenSharing$,
    this.isPictureInPicture$,
  ]).pipe(
    map(
      ([isScreenSharing, isPictureInPicture]) =>
        isScreenSharing || isPictureInPicture,
    ),
  );

  public declineReasonText$ = combineLatest([
    this.currentContact$,
    this.store.pipe(select(selectDeclineReason)),
  ]).pipe(
    map(([currentContact, declineReason]) =>
      declineReason
        ? $localize`:connect|Connect call decline@@connectCallDecline:${
            currentContact?.displayName
          } has ${
            declineReason === ConnectDeclineReason.TIMEOUT
              ? 'missed'
              : 'declined'
          } the call`
        : null,
    ),
  );

  public elapsedTime$ = this.remoteParticipant$.pipe(
    filter((participant) => isNonNullish(participant)),
    switchMapTo(
      timer(0, 1000).pipe(
        switchMap((miliseconds) => {
          if (this.elapsedSeconds === 60) {
            this.elapsedSeconds = 0;
          } else {
            this.elapsedSeconds++;
          }
          return of(transformToElapsedTime(this.elapsedSeconds, miliseconds));
        }),
      ),
    ),
  );

  public microphoneOnText = $localize`:Connect|Turn microphone on tooltip@@connectMicrophoneOnTooltip:Unmute`;
  public microphoneOffText = $localize`:Connect|Turn microphone off tooltip@@connectMicrophoneOffTooltip:Mute`;
  public cameraOnText = $localize`:Connect|Turn camera on tooltip@@connectCameraOnTooltip:Turn Camera On`;
  public cameraOffText = $localize`:Connect|Turn camera off tooltip@@connectCameraOffTooltip:Turn Camera Off`;
  public screenShareOnText = $localize`:Connect|Start screen share@@connectScreenShareOnTooltip:Start screen share`;
  public screenShareOffText = $localize`:Connect|Stop screen share@@connectScreenShareOffTooltip:Stop screen share`;
  public pictureInPictureOnText = $localize`:Connect|Start picture in picture@@connectPictureInPictureOnTooltip:Start picture in picture`;
  public pictureInPictureOffText = $localize`:Connect|Stop picture in picture@@connectPictureInPictureOffTooltip:Exit picture in picture`;

  @ViewChild('localParticipant', { read: ElementRef })
  public localParticipant: ElementRef<HTMLElement>;

  @ViewChild('remoteParticipant')
  public remoteParticipant: ParticipantComponent;

  public participantVideoElement: HTMLVideoElement;
  public animationParams$: Observable<AnimationParams>;
  public isDesktop$: Observable<boolean>;

  public elapsedSeconds = 0;
  public settingsOpened = false;

  private destroy$ = new Subject<void>();

  private isMobile$ = this.breakpointObserver
    .observe(BREAKPOINT)
    .pipe(map((value) => value.matches));

  constructor(
    private store: Store,
    private connectVideoStore: ConnectVideoStore,
    private breakpointObserver: BreakpointObserver,
  ) {}

  public ngOnInit(): void {
    this.subscribeConnectParams();
    this.isDesktop$ = this.isMobile$.pipe(map((value) => !value));
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public finalizeCall() {
    this.connectVideoStore.disconnectRoom();
    this.store.dispatch(setDeclineReason({ reason: undefined }));
  }

  public toggleCamera() {
    this.connectVideoStore.toggleMedia(TwilioDeviceKind.VIDEO);
  }

  public toggleMicrophone() {
    this.connectVideoStore.toggleMedia(TwilioDeviceKind.AUDIO);
  }

  public toggleScreenShare(isScreenSharing: boolean) {
    this.connectVideoStore.toggleScreenShare();
    if (isScreenSharing) {
      this.removeLocalParticipantScreenShareVideo();
    }
  }

  public togglePictureInPicture(value: boolean) {
    this.connectVideoStore.togglePictureInPicture(value);
  }

  public handleClose(value: boolean) {
    if (!value) {
      this.settingsOpened = false;
    }
  }

  public openSettings(evt: MouseEvent) {
    evt.stopPropagation();
    this.settingsOpened = true;
  }

  public handleClickOutside() {
    if (this.settingsOpened) {
      this.settingsOpened = false;
    }
  }

  private subscribeConnectParams() {
    this.store
      .pipe(
        select(selectConnectParams),
        filter(({ token, room }) => isNonNullish(room) && isNonNullish(token)),
        takeUntil(this.destroy$),
        take(1),
      )
      .subscribe(() => {
        this.store.dispatch(setVideoLoading({ loading: false }));
        this.connectVideoStore.checkMediaPermissions();
      });
  }

  public async onAudioOutputChange(deviceId: string) {
    await (
      this.remoteParticipant.audioRef.nativeElement as CustomHTMLAudioElement
    ).setSinkId(deviceId);
  }

  public onAudioInputChange(deviceId: string) {
    this.connectVideoStore.updateAudioDevice(deviceId);
  }

  public onVideoInputChange(deviceId: string) {
    this.connectVideoStore.updateVideoDevice(deviceId);
  }

  /**
   * Due to https://github.com/twilio/twilio-video.js/issues/656
   * we need to remove the localParticipant screen share video
   * element
   */
  private removeLocalParticipantScreenShareVideo() {
    const video =
      this.localParticipant.nativeElement.querySelectorAll('video')[1];
    video.remove();
  }
}
